/**
 * Select — label + native <select>, mirroring the @obrava/ui Field look. Used
 * for the enum dropdowns (sector / plan / language / status). Token-driven; no
 * hex / size literals.
 */

import React from 'react';
import { useTheme } from '@obrava/ui';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'style' | 'children'> {
  readonly label: string;
  readonly options: readonly SelectOption[];
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly containerStyle?: React.CSSProperties | undefined;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(function Select(
  props,
  ref,
): React.ReactElement {
  const {
    label,
    options,
    hint,
    error,
    containerStyle,
    id,
    disabled = false,
    onFocus,
    onBlur,
    ...rest
  } = props;

  const theme = useTheme();
  const [focused, setFocused] = React.useState(false);
  const reactId = React.useId();
  const fieldId = id ?? reactId;
  const describedById = `${fieldId}-desc`;

  const hasError = Boolean(error);
  const borderColor = hasError
    ? theme.color.danger
    : focused
      ? theme.color.borderFocus
      : theme.color.border;

  const labelStyle: React.CSSProperties = {
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.footnote,
    fontWeight: theme.typography.weight.semibold,
    color: theme.color.textPrimary,
    marginBottom: theme.spacing.space[3],
  };

  const selectStyle: React.CSSProperties = {
    boxSizing: 'border-box',
    width: '100%',
    height: theme.spacing.size.fieldHeight,
    paddingInline: theme.spacing.space[4],
    backgroundColor: disabled ? theme.color.surfaceSunken : theme.color.surface,
    color: disabled ? theme.color.textDisabled : theme.color.textPrimary,
    border: `1px solid ${borderColor}`,
    borderRadius: theme.radii.radius.field,
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.body,
    lineHeight: theme.typography.lineHeight.normal,
    outline: 'none',
    boxShadow: focused && !hasError ? theme.radii.shadow.focusRing : 'none',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    appearance: 'none',
  };

  const messageStyle: React.CSSProperties = {
    fontFamily: theme.typography.family.sans,
    fontSize: theme.typography.size.caption,
    color: hasError ? theme.color.dangerText : theme.color.textTertiary,
    marginTop: theme.spacing.space[2],
  };

  const message = error ?? hint;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', ...containerStyle }}>
      <label htmlFor={fieldId} style={labelStyle}>
        {label}
      </label>
      <select
        {...rest}
        ref={ref}
        id={fieldId}
        disabled={disabled}
        aria-invalid={hasError}
        aria-describedby={message ? describedById : undefined}
        style={selectStyle}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {message ? (
        <span id={describedById} style={messageStyle}>
          {message}
        </span>
      ) : null}
    </div>
  );
});
